/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { jsx } from '@emotion/react';

import * as React from 'react';
import { graphql } from 'react-apollo';

import classNames from 'classnames';
import gql from 'graphql-tag';
import { truncate } from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import type { LegacyContextType } from 'types/legacy-context-types';

import Imgix from 'js/components/Imgix';
import getModernContext from 'js/lib/getModernContext';
import user from 'js/lib/user';

import { Hidden } from '@coursera/cds-core';

import ContextButton from 'bundles/enterprise-admin-home/components/ContextButton';
import type { ContextProps } from 'bundles/enterprise-admin-home/components/ContextButton';
import type {
  PartnerMembershipsQuery_PartnerMembershipsV1Resource_findByUser_elements as PartnerId,
  PartnerMembershipsQuery as PartnerMembershipsQueryData,
  PartnerMembershipsQueryVariables,
} from 'bundles/enterprise-admin-home/components/__generated__/PartnerMembershipsQuery';
import NavigationHeader from 'bundles/enterprise-admin-navigation/components/NavigationHeader';
import AdminNavigationPartners from 'bundles/enterprise-admin-navigation/queries/AdminNavigationPartners.graphql';
import type {
  AdminNavigationPartnersQuery,
  AdminNavigationPartnersQueryVariables,
} from 'bundles/enterprise-admin-navigation/queries/__generated__/AdminNavigationPartners';
import {
  getHelpLinks,
  getMenuLinks,
  getSettingsLinks,
  logout,
} from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';
import type { EnterpriseAdminPermissionsContextProps } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import { EnterpriseAdminPermissionsContext } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';
import EnterprisePermissionsRequired from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsRequired';
import { isSelfServiceSsoEnabled } from 'bundles/enterprise-admin-sso-settings/SelfServiceSsoExperiment';
import { withProductFeatures } from 'bundles/enterprise-admin/features';
import type { ThirdPartyOrganization as AdminDashboardOrg } from 'bundles/enterprise-admin/types';
import DefaultOrganizationLogo from 'bundles/enterprise-legacy-learner-home/components/DefaultOrganizationLogo';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import ListWithKeyboardControls from 'bundles/phoenix/components/a11y/ListWithKeyboardControls';

import _t from 'i18n!nls/page';

import 'css!./__styles__/AdminNavigationDropdown';

type PropsFromCaller = {
  isDirectSale: boolean;
  isSelfServe: boolean;
  thirdPartyOrganization: AdminDashboardOrg;
  thirdPartyOrganizations: Array<AdminDashboardOrg>;
  thirdPartySlug?: string; // An optional prop passed by authoring, thirdpartyslug isn't a router param.
  renderCoachLauncher?: () => React.ReactNode | null;
};

type PropsFromPartnerIdsGraphql = {
  myPartnerIds: string[];
};

type PropsFromPartnersGraphql = {
  myPartners: {
    name: string;
    squareLogo?: string | null;
    rectangularLogo?: string | null;
  }[];
};

type PropsFromWithProductFeatures = {
  enableSelfServiceSso: boolean;
};
type PropsFromEnterprisePermissionsContext = {
  enterpriseAdminPermissionsContext: EnterpriseAdminPermissionsContextProps;
};
type Props = PropsFromCaller &
  PropsFromPartnersGraphql &
  PropsFromWithProductFeatures &
  PropsFromEnterprisePermissionsContext;

type State = {
  showNavMenu: boolean;
};

class AdminNavigationDropdown extends React.PureComponent<Props, State> {
  declare node: HTMLDivElement | null | undefined;

  static contextTypes = {
    router: PropTypes.object.isRequired,
    enableSkillsPlanningForOrg: PropTypes.bool.isRequired,
    enableNewLearnersPage: PropTypes.bool.isRequired,
  };

  declare context: LegacyContextType<typeof AdminNavigationDropdown.contextTypes>;

  state = {
    showNavMenu: false,
  };

  componentDidUpdate(prevProps: Props, { showNavMenu: prevShowNavMenu }: State) {
    const { showNavMenu } = this.state;
    if (showNavMenu !== prevShowNavMenu) {
      if (showNavMenu) {
        // attach / remove event listener to monitor click and close/open menu accordingly.
        // When user click anywhere  after menu is opened, toggleMenu() will close when
        // user clicked outside of the DropDown
        document.addEventListener('click', this.handleClick, false);
      } else {
        document.removeEventListener('click', this.handleClick, false);
      }
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClick, false);
  }

  handleClick = (e: MouseEvent) => {
    if (this.node && this.node.contains(e.target as Node)) {
      return;
    }
    this.toggleMenu();
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleBlur = (e: any) => {
    const { showNavMenu } = this.state;
    if (this.node && this.node.contains(e.relatedTarget)) {
      return;
    }
    if (showNavMenu) {
      this.toggleMenu();
    }
  };

  getContexts = () => {
    const { myPartners, thirdPartyOrganizations, thirdPartyOrganization, thirdPartySlug } = this.props;
    const { photo_120: photoUrl, fullName } = user.get();
    const contexts: any[] = []; // eslint-disable-line @typescript-eslint/no-explicit-any

    if (user.isSuperuser()) {
      contexts.push({
        href: '/admin/',
        header: _t('Educator Admin'),
        label: 'Educator Admin',
        showLabel: false,
        photoUrl: null,
      });
    } else if (myPartners && myPartners.length > 0) {
      myPartners.forEach(({ name, squareLogo, rectangularLogo }) => {
        contexts.push({
          href: '/admin/',
          header: _t('Educator Admin'),
          label: truncate(name, { length: 20 }), //  if the label is too wide it creates horizontal overflow on mobile
          showLabel: true,
          photoUrl: squareLogo || rectangularLogo,
        });
      });
    }

    // if the authoring side is using this component, we want to have a link back to enterprise
    (thirdPartySlug
      ? thirdPartyOrganizations
      : thirdPartyOrganizations.filter((tpo) => tpo.id !== thirdPartyOrganization.id)
    ).forEach(({ name, slug, squareLogo, rectangularLogo, primaryColor, iconColor }) => {
      contexts.push({
        key: slug,
        href: `/o/${slug}/admin`,
        header: _t('Organization Admin'),
        label: truncate(name, { length: 20 }), //  if the label is too wide it creates horizontal overflow on mobile
        showLabel: true,
        photoUrl: squareLogo || rectangularLogo,
        primaryColor,
        iconColor,
      });
    });

    contexts.push({
      href: '/',
      header: _t('My Coursera'),
      label: fullName,
      showLabel: true,
      photoUrl,
    });

    return contexts;
  };

  getNavigationImage = () => {
    const { thirdPartyOrganization } = this.props;
    const { name, squareLogo, rectangularLogo, primaryColor, iconColor } = thirdPartyOrganization;
    const thirdPartyOrganizationLogo = squareLogo || rectangularLogo;

    let profileImage = <DefaultOrganizationLogo size={44} thirdPartyOrganization={{ name, primaryColor, iconColor }} />;
    if (thirdPartyOrganizationLogo) {
      profileImage = (
        <Imgix alt={_t('Organization Logo')} src={thirdPartyOrganizationLogo} maxWidth={44} maxHeight={44} />
      );
    }
    return <div css={{ width: 44, height: 44 }}>{profileImage}</div>;
  };

  toggleMenu = () => {
    this.setState((prevState) => ({
      showNavMenu: !prevState.showNavMenu,
    }));
  };

  renderMenuLinks() {
    const {
      router: { params, location },
      enableSkillsPlanningForOrg,
      enableNewLearnersPage,
    } = this.context;
    const { thirdPartySlug, thirdPartyOrganization } = this.props;
    const enableNewAdminHome = EnterpriseExperiments.get('enableNewAdminHome')?.includes(thirdPartyOrganization.slug);

    return getMenuLinks(
      { thirdPartySlug, ...params },
      enableSkillsPlanningForOrg,
      false,
      thirdPartyOrganization.id,
      enableNewLearnersPage,
      enableNewAdminHome
    ).map((item, index) => {
      const { id, href, text, requiredPermissions, phaseOneOverride } = item;

      const menuLinkItem = (
        <li key={id} className={classNames('list-item')}>
          <a className="menu-item" href={`${href}${location.search}`} data-e2e={`Menu_Link_Dropdown~${text}`}>
            {text}
          </a>
        </li>
      );

      return requiredPermissions ? (
        <EnterprisePermissionsRequired
          domain={requiredPermissions.domain}
          domainId={requiredPermissions.domainId}
          subdomain={requiredPermissions.subdomain}
          verb={requiredPermissions.verb}
          phaseOneOverride={phaseOneOverride}
        >
          {menuLinkItem}
        </EnterprisePermissionsRequired>
      ) : (
        menuLinkItem
      );
    });
  }

  enableWesBilling = () => {
    const { isSelfServe, enterpriseAdminPermissionsContext, thirdPartyOrganization } = this.props;
    return (
      isSelfServe &&
      enterpriseAdminPermissionsContext.hasPermission({
        domain: 'ORGANIZATION',
        domainId: thirdPartyOrganization.id,
        subdomain: 'SELF_SERVE_CONTRACTS',
        verb: 'MANAGE',
      })
    );
  };

  renderSettingsLinks = () => {
    const { enableSelfServiceSso, thirdPartySlug, enterpriseAdminPermissionsContext, thirdPartyOrganization } =
      this.props;
    const {
      router: { params },
      enableNewLearnersPage,
    } = this.context;

    return getSettingsLinks(
      { thirdPartySlug, ...params },
      this.enableWesBilling(),
      thirdPartyOrganization.loginMethod === 'SAML' && enableSelfServiceSso,
      enterpriseAdminPermissionsContext.getIsOrganizationDomainAdmin(),
      thirdPartyOrganization.id,
      enableNewLearnersPage
    ).map((item, index) => {
      const { href, text, requiredPermissions, phaseOneOverride } = item;

      const linkItem = (
        <li key={text} className={classNames('list-item', { 'upper-padding': index === 0 })}>
          <a className="menu-item" href={href} data-e2e={`Settings_Link~${text}`}>
            {text}
          </a>
        </li>
      );

      return requiredPermissions ? (
        <EnterprisePermissionsRequired
          domain={requiredPermissions.domain}
          domainId={requiredPermissions.domainId}
          subdomain={requiredPermissions.subdomain}
          verb={requiredPermissions.verb}
          phaseOneOverride={phaseOneOverride}
        >
          {linkItem}
        </EnterprisePermissionsRequired>
      ) : (
        linkItem
      );
    });
  };

  renderHelpLinks = () => {
    const {
      isDirectSale,
      enableSelfServiceSso,
      thirdPartyOrganization,
      thirdPartySlug,
      enterpriseAdminPermissionsContext,
    } = this.props;
    const {
      router: { params },
      enableNewLearnersPage,
    } = this.context;

    const settingsLinks = getSettingsLinks(
      { thirdPartySlug, ...params },
      this.enableWesBilling(),
      thirdPartyOrganization.loginMethod === 'SAML' && enableSelfServiceSso,
      enterpriseAdminPermissionsContext.getIsOrganizationDomainAdmin(),
      thirdPartyOrganization.id,
      enableNewLearnersPage
    );
    return getHelpLinks(isDirectSale, { thirdPartySlug, ...params }).map((item, index) => {
      return (
        <li
          key={item.text}
          className={classNames('list-item', { 'upper-padding': index === 0 && settingsLinks.length === 0 })}
        >
          <a
            className="menu-item"
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            data-e2e={`Help_Link~${item.text}`}
          >
            {item.text}
          </a>
        </li>
      );
    });
  };

  renderLogOutLink = () => {
    return (
      <li className="list-item" key="logout">
        <button
          css={{ backgroundColor: 'var(--cds-color-neutral-background-primary)' }}
          className="menu-item"
          data-e2e="Log_Out_Link"
          type="button"
          onClick={() => logout()}
        >
          {_t('Log Out')}
        </button>
      </li>
    );
  };

  renderDropDownList = (children?: React.ReactNode) => {
    const { showNavMenu } = this.state;
    if (!showNavMenu) {
      return null;
    }
    const contexts = this.getContexts();
    const contextButtons = contexts.map(
      ({ key, href, header, label, photoUrl, showLabel, primaryColor, iconColor }: ContextProps) => (
        <ContextButton
          key={key}
          href={href}
          header={header}
          label={label}
          photoUrl={photoUrl}
          showLabel={showLabel}
          primaryColor={primaryColor}
          iconColor={iconColor}
        />
      )
    );

    return (
      <ListWithKeyboardControls
        className="list"
        role="menu"
        aria-label={_t('Admin Navigation Menu')}
        aria-labelledby="adminNavigationMenu"
        onEsc={this.toggleMenu}
        activateClick
        allowDefaultOnEnter
      >
        {contextButtons}
        {children}

        {children && <hr key="menu-separator" className="list-separator" />}
        {this.renderSettingsLinks()}
        {this.renderHelpLinks()}
        <hr key="list-separator" className="list-separator" />
        {this.renderLogOutLink()}
      </ListWithKeyboardControls>
    );
  };

  renderDropDownListWithMenu = () => {
    return this.renderDropDownList(
      <>
        {this.props.renderCoachLauncher?.()}
        {this.renderMenuLinks()}
      </>
    );
  };

  render() {
    const { thirdPartyOrganization } = this.props;
    const { showNavMenu } = this.state;

    return (
      <div
        id="adminNavigationMenu"
        className="rc-AdminNavigationDropdown"
        css={{
          backgroundColor: 'var(--cds-color-neutral-background-primary)',
          fontSize: '14px',
          height: '100%',
          maxWidth: '290px',
          marginLeft: '26px',
          zIndex: 10000,
        }}
        ref={(ref) => {
          this.node = ref;
        }}
      >
        <NavigationHeader
          header={_t('Admin')}
          label={truncate(thirdPartyOrganization.name, { length: 20 })}
          profileImage={this.getNavigationImage()}
          isActive={showNavMenu}
          isCaretUp={showNavMenu}
          onClick={this.toggleMenu}
        />
        <div data-testid="mobile">
          <Hidden mdUp>{this.renderDropDownListWithMenu()}</Hidden>
        </div>
        <div data-testid="desktop">
          <Hidden smDown>{this.renderDropDownList()}</Hidden>
        </div>
      </div>
    );
  }
}

const PartnerMembershipsQuery = gql`
  query PartnerMembershipsQuery($userId: String!) {
    PartnerMembershipsV1Resource {
      findByUser(userId: $userId, partnerRole: "PARTNER_ADMIN") {
        elements {
          id
          partnerId {
            maestroId
          }
        }
      }
    }
  }
`;

export default compose<Props, PropsFromCaller>(
  graphql<PropsFromCaller, PartnerMembershipsQueryData, PartnerMembershipsQueryVariables, PropsFromPartnerIdsGraphql>(
    PartnerMembershipsQuery,
    {
      options: () => ({
        variables: { userId: `${user.get().id}` },
      }),
      props: ({ data }) => {
        const nonNullPartnerIds = (data?.PartnerMembershipsV1Resource?.findByUser?.elements || []).filter(
          (el) => !!el
        ) as PartnerId[];
        const myPartnerIds = nonNullPartnerIds.map((el) => String(el.partnerId.maestroId));
        return {
          myPartnerIds,
        };
      },
    }
  ),
  graphql<
    PropsFromPartnerIdsGraphql,
    AdminNavigationPartnersQuery,
    AdminNavigationPartnersQueryVariables,
    PropsFromPartnersGraphql
  >(AdminNavigationPartners, {
    skip: ({ myPartnerIds }) => !myPartnerIds || myPartnerIds.length === 0,
    options: ({ myPartnerIds }) => ({
      variables: { ids: myPartnerIds },
      context: { clientName: 'gatewayGql' },
    }),
    props: ({ data }) => {
      const myPartners = data?.Partner?.queryByIds ?? [];
      return {
        myPartners,
      };
    },
  }),
  withProductFeatures<PropsFromWithProductFeatures, PropsFromCaller>(
    ({ thirdPartyOrganization }) => ({ idType: 'Organization', id: thirdPartyOrganization.id }),
    ({ features }) => ({
      enableSelfServiceSso: features.get('enterprise', 'enableSelfServiceSso') && isSelfServiceSsoEnabled(),
    })
  ),
  getModernContext('enterpriseAdminPermissionsContext', EnterpriseAdminPermissionsContext)
)(AdminNavigationDropdown);
